import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { TrashIcon, CalculatorIcon, DocumentTextIcon, EyeIcon, PencilIcon, BuildingOfficeIcon as Building2 } from '@heroicons/react/24/outline';
import { PlusCircle as Plus } from 'lucide-react';
import { getEstimates, deleteEstimate } from '../../services/estimateService';
import { getProperties, deleteProperty } from '../../services/propertyService';
import { EstimateWithId } from '../../types/estimation';
import { Property } from '../../types/property';
import { useLoadingState } from '../../hooks/useLoadingState';
import LoadingSpinner from '../ui/LoadingSpinner';
import ErrorMessage from '../ui/ErrorMessage';
import EstimateTypeBadge from './EstimateTypeBadge';
import ConfirmDialog from '../ui/ConfirmDialog';
import { useAuth } from '../../contexts/AuthContext';
import { useUserProfile } from '../../hooks/useUserProfile';

const PROPERTY_TYPE_TRANSLATIONS = {
  'house': 'Maison',
  'apartment': 'Appartement', 
  'building': 'Immeuble'
} as const;

interface EstimatesListProps {
 userId?: string;
}

const EstimatesList: React.FC<EstimatesListProps> = ({ userId }) => {
 const navigate = useNavigate();
 const [estimates, setEstimates] = useState<EstimateWithId[]>([]);
 const [properties, setProperties] = useState<Property[]>([]);
 const { isLoading, error, setLoading, handleError, clearError } = useLoadingState();
 const [deleteConfirmation, setDeleteConfirmation] = useState<{
   isOpen: boolean;
   estimateId: string | null;
 }>({ isOpen: false, estimateId: null });
 const [deletePropertyConfirm, setDeletePropertyConfirm] = useState<{
   isOpen: boolean;
   propertyId: string | null;
 }>({ isOpen: false, propertyId: null });

 useEffect(() => {
   if (userId) {
     loadData();
   }
 }, [userId]);

 const loadData = async () => {
   if (!userId) return;
   try {
     setLoading(true);
     clearError();
     const [estimatesData, propertiesData] = await Promise.all([
       getEstimates(userId),
       getProperties(userId)
     ]);
     
     setEstimates(estimatesData.sort((a, b) => 
       new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
     ));
     
     setProperties(propertiesData.sort((a, b) => 
       new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
     ));
   } catch (error) {
     handleError(error);
   } finally {
     setLoading(false);
   }
 };

 const handleDeleteClick = (estimateId: string) => {
   setDeleteConfirmation({ isOpen: true, estimateId });
 };

 const handleConfirmDelete = async () => {
   if (!userId || !deleteConfirmation.estimateId) return;
   
   try {
     setLoading(true);
     clearError();
     await deleteEstimate(userId, deleteConfirmation.estimateId);
     setEstimates(estimates.filter(e => e.id !== deleteConfirmation.estimateId));
     setDeleteConfirmation({ isOpen: false, estimateId: null });
   } catch (error) {
     handleError(error);
   } finally {
     setLoading(false);
   }
 };

 const handleDeletePropertyClick = (propertyId: string) => {
   setDeletePropertyConfirm({ isOpen: true, propertyId });
 };

 const handleConfirmDeleteProperty = async (propertyId: string | null) => {
   if (!userId || !propertyId) return;
   
   try {
     setLoading(true);
     clearError();
     await deleteProperty(userId, propertyId);
     setProperties(properties.filter(p => p.id !== propertyId));
     setDeletePropertyConfirm({ isOpen: false, propertyId: null });
   } catch (error) {
     handleError(error);
   } finally {
     setLoading(false);
   }
 };

 const handleViewEstimate = (estimate: EstimateWithId) => {
   localStorage.setItem('viewEstimate', JSON.stringify(estimate));
   navigate(`/estimation/${estimate.id}/view`);
 };

 const handleEditEstimate = (estimate: EstimateWithId) => {
   localStorage.setItem('editEstimate', JSON.stringify(estimate));
   navigate(`/estimation/${estimate.id}/edit`);
 };

 const formatAddress = (estimate: EstimateWithId) => {
   const location = estimate.type === 'detailed' 
     ? estimate.description?.location 
     : estimate.location;

   if (!location) return 'Adresse non spécifiée';
   
   const parts = [
     location.address,
     location.postcode,
     location.city
   ].filter(Boolean);
   
   return parts.join(' - ');
 };

 if (isLoading) return <LoadingSpinner />;
 if (error) return <ErrorMessage message={error} />;

 if (estimates.length === 0 && properties.length === 0) {
   return (
     <div className="text-center py-8">
       <p className="text-gray-500">Aucune estimation ni bien trouvé</p>
       <div className="mt-4 space-x-4">
         <Link
           to="/quick-estimate"
           className="btn btn-secondary inline-flex items-center"
         >
           <CalculatorIcon className="h-4 w-4 mr-2" />
           Estimation rapide
         </Link>
         <Link
           to="/estimation/nouvelle"
           className="btn btn-primary inline-flex items-center"
         >
           <DocumentTextIcon className="h-4 w-4 mr-2" />
           Estimation détaillée
         </Link>
       </div>
     </div>
   );
 }

 return (
   <>
     {/* Section des propriétés sans estimation */}
     {properties.length > 0 && (
       <div className="mb-8">
         <h2 className="text-xl font-semibold mb-4">Biens à estimer</h2>
         <div className="space-y-4">
           {properties.filter(p => !p.estimateId).map((property) => (
             <div
               key={property.id}
               className="bg-white rounded-lg border p-6 hover:border-primary/20 transition-colors"
             >
               <div className="flex items-start justify-between">
                 <div>
                   <div className="flex items-center space-x-2">
                     <Building2 className="h-5 w-5 text-neutral-500" />
                     <h3 className="text-lg font-medium">
                       {PROPERTY_TYPE_TRANSLATIONS[property.type as keyof typeof PROPERTY_TYPE_TRANSLATIONS] || property.type}
                     </h3>
                   </div>
                   <p className="text-neutral-500 mt-1">
                     {property.postcode} {property.city}
                   </p>
                   <p className="text-sm text-neutral-500 mt-2">
                    {PROPERTY_TYPE_TRANSLATIONS[property.type as keyof typeof PROPERTY_TYPE_TRANSLATIONS]} - {property.area} m²
                   </p>
                 </div>
                 <div className="flex space-x-2">
                   <Link
                      to={`/estimation/${property.id}/edit`}
                      onClick={(e) => {
                        e.preventDefault();
                        localStorage.setItem('editEstimate', JSON.stringify({
                          id: property.id,
                          type: 'detailed',
                          propertyId: property.id,
                          description: {
                            location: {
                              address: property.address,
                              postcode: property.postcode,
                              city: property.city
                            },
                            propertyType: property.type,
                            area: property.area
                          }
                        }));
                        navigate(`/estimation/${property.id}/edit`);
                      }}
                      className="btn btn-primary flex items-center"
                    >
                     <Plus className="h-4 w-4 mr-2" />
                     Faire une estimation travaux détaillée
                   </Link>
                   <button
                     onClick={() => handleDeletePropertyClick(property.id)}
                     className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-full"
                     title="Supprimer"
                   >
                     <TrashIcon className="h-5 w-5" />
                   </button>
                 </div>
               </div>
             </div>
           ))}
         </div>
       </div>
     )}

     {/* Section des estimations */}
     {estimates.length > 0 && (
       <div>
         <h2 className="text-xl font-semibold mb-4">Estimations réalisées</h2>
         <div className="space-y-4">
           {estimates.map((estimate) => (
             <div
               key={`${estimate.id}-${estimate.createdAt}`}
               className="bg-white rounded-lg border p-4 hover:border-primary/20 transition-colors"
             >
               <div className="flex items-center justify-between mb-2">
                 <div className="flex items-center space-x-3">
                   <h3 className="text-lg font-medium">
                     {formatAddress(estimate)}
                   </h3>
                   <EstimateTypeBadge type={estimate.type || 'quick'} />
                 </div>
                 <div className="flex items-center space-x-2">
                   <button
                     onClick={() => handleViewEstimate(estimate)}
                     className="p-2 text-blue-600 hover:bg-blue-50 rounded-full"
                     title="Consulter"
                   >
                     <EyeIcon className="h-5 w-5" />
                   </button>
                   {estimate.type === 'detailed' && (
                     <button
                       onClick={() => handleEditEstimate(estimate)}
                       className="p-2 text-green-600 hover:bg-green-50 rounded-full"
                       title="Modifier"
                     >
                       <PencilIcon className="h-5 w-5" />
                     </button>
                   )}
                   <button
                     onClick={() => handleDeleteClick(estimate.id)}
                     className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-full"
                     title="Supprimer"
                   >
                     <TrashIcon className="h-5 w-5" />
                   </button>
                 </div>
               </div>

               <div className="text-sm text-gray-500">
                 <p>Créée le {new Date(estimate.createdAt).toLocaleDateString()}</p>
                 {estimate.priceRange && (
                   <p className="font-medium text-primary mt-2">
                     Estimation : {new Intl.NumberFormat('fr-FR', {
                       style: 'currency',
                       currency: 'EUR',
                       maximumFractionDigits: 0
                     }).format(estimate.priceRange.min)}
                   </p>
                 )}
               </div>
             </div>
           ))}
         </div>
       </div>
     )}

     <ConfirmDialog
       isOpen={deleteConfirmation.isOpen}
       title="Confirmer la suppression"
       message="Êtes-vous sûr de vouloir supprimer cette estimation ? Cette action est irréversible."
       confirmLabel="Supprimer"
       cancelLabel="Annuler"
       onConfirm={handleConfirmDelete}
       onCancel={() => setDeleteConfirmation({ isOpen: false, estimateId: null })}
       variant="danger"
     />

     <ConfirmDialog
       isOpen={deletePropertyConfirm.isOpen}
       title="Supprimer ce bien ?"
       message="Cette action est irréversible et le crédit utilisé ne sera pas restitué. Êtes-vous sûr de vouloir continuer ?"
       confirmLabel="Supprimer définitivement"
       cancelLabel="Annuler"
       onConfirm={() => handleConfirmDeleteProperty(deletePropertyConfirm.propertyId)}
       onCancel={() => setDeletePropertyConfirm({ isOpen: false, propertyId: null })}
       variant="danger"
     />
   </>
 );
};

export default EstimatesList;